import { useEffect, useState } from 'react'
import { Modal, Button, Form, Row, Col, Accordion } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import DatePicker from 'react-multi-date-picker'
import { toast } from 'react-toastify'

import {
  CREATE_KANBAN_TASK,
  CREATE_KANBAN_TASK_ATTACHMENT,
  CREATE_KANBAN_COMMENT,
  UPDATE_KANBAN_TASK,
  DELETE_KANBAN_TASK_ATTACHMENT,
  UPDATE_KANBAN_COMMENT,
  DELETE_KANBAN_COMMENT,
  ADD_KANBAN_CHECKLIST_ITEM,
  TOGGLE_KANBAN_CHECKLIST_ITEM,
  COMPLETE_KANBAN_TASK,
  UPDATE_KANBAN_CHECKLIST_ITEM,
  DELETE_KANBAN_CHECKLIST_ITEM,
  ADD_KANBAN_PARTICIPANT,
  REMOVE_KANBAN_PARTICIPANT,
} from '../../../gql/mutations/kanban'
import { GET_KANBAN_TASK_DETAIL } from '../../../gql/queries/kanban'
import { GET_ALL_USERS_LIST } from '../../../gql/queries/usersQuery'
import {
  ICreateKanbanTaskRes,
  IKanbanStage,
  IKanbanTask,
  ICreateKanbanTaskAttachmentRes,
  ICreateKanbanCommentRes,
  IUpdateKanbanTaskRes,
  IKanbanTaskDetailRes,
  IDeleteKanbanTaskAttachmentRes,
  IUpdateKanbanCommentRes,
  IDeleteKanbanCommentRes,
  IAddKanbanChecklistItemRes,
  IToggleKanbanChecklistItemRes,
  ICompleteKanbanTaskRes,
  IUpdateKanbanChecklistItemRes,
  IDeleteKanbanChecklistItemRes,
  IAddKanbanParticipantRes,
  IRemoveKanbanParticipantRes,
} from '../../../types/kanban'
import { ModalAddComment } from './ModalAddComment'
import { useGetUser } from '../../../store/userStore'
import { parseId } from '../../../helpers'
import { prependBackendUrl } from '../../../gql/client'

interface Props {
  show: boolean
  mode: 'create' | 'edit'
  workflowId?: string
  stage?: IKanbanStage | null
  task?: IKanbanTask | null
  availableGroups?: { id: string; name: string }[]
  canManageParticipants?: boolean
  readOnly?: boolean
  onClose: () => void
  onSuccess: () => void
}

export const ModalTaskForm: React.FC<Props> = ({
  show,
  mode,
  workflowId,
  stage,
  task,
  availableGroups = [],
  canManageParticipants = true,
  readOnly = false,
  onClose,
  onSuccess,
}) => {
  const currentUser = useGetUser((s) => s.user)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [tagColor, setTagColor] = useState('')
  const [labelText, setLabelText] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [priority, setPriority] = useState('NORMAL')
  const [isTaskCompleted, setIsTaskCompleted] = useState(false)
  const [completedAtLabel, setCompletedAtLabel] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<
    Array<{ file: File; preview: string; description?: string }>
  >([])
  const [pendingComments, setPendingComments] = useState<
    Array<{ text: string; createdAt: Date; authorName: string }>
  >([])
  const [isDragging, setIsDragging] = useState(false)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [editingComment, setEditingComment] = useState<{ id: string; body: string } | null>(null)
  const [newChecklistTitle, setNewChecklistTitle] = useState('')
  const [editingChecklist, setEditingChecklist] = useState<{ id: string; title: string } | null>(null)
  const [selectedParticipantId, setSelectedParticipantId] = useState('')

  const [createTask, { loading: creating }] = useMutation<ICreateKanbanTaskRes>(CREATE_KANBAN_TASK)
  const [updateTask, { loading: updating }] = useMutation<IUpdateKanbanTaskRes>(UPDATE_KANBAN_TASK)
  const [uploadAttachment] = useMutation<ICreateKanbanTaskAttachmentRes>(
    CREATE_KANBAN_TASK_ATTACHMENT
  )
  const [createComment] = useMutation<ICreateKanbanCommentRes>(CREATE_KANBAN_COMMENT)
  const [deleteAttachmentMutation] = useMutation<IDeleteKanbanTaskAttachmentRes>(
    DELETE_KANBAN_TASK_ATTACHMENT
  )
  const [updateCommentMutation, { loading: savingComment }] =
    useMutation<IUpdateKanbanCommentRes>(UPDATE_KANBAN_COMMENT)
  const [deleteCommentMutation, { loading: deletingComment }] =
    useMutation<IDeleteKanbanCommentRes>(DELETE_KANBAN_COMMENT)
  const [addChecklistItemMutation, { loading: addingChecklist }] =
    useMutation<IAddKanbanChecklistItemRes>(ADD_KANBAN_CHECKLIST_ITEM)
  const [toggleChecklistItemMutation] = useMutation<IToggleKanbanChecklistItemRes>(
    TOGGLE_KANBAN_CHECKLIST_ITEM
  )
  const [updateChecklistItemMutation, { loading: updatingChecklist }] =
    useMutation<IUpdateKanbanChecklistItemRes>(UPDATE_KANBAN_CHECKLIST_ITEM)
  const [deleteChecklistItemMutation, { loading: deletingChecklist }] =
    useMutation<IDeleteKanbanChecklistItemRes>(DELETE_KANBAN_CHECKLIST_ITEM)
  const [togglingChecklistId, setTogglingChecklistId] = useState<string | null>(null)
  const [completeTaskMutation, { loading: completingTask }] =
    useMutation<ICompleteKanbanTaskRes>(COMPLETE_KANBAN_TASK)
  const [addParticipantMutation, { loading: addingParticipant }] =
    useMutation<IAddKanbanParticipantRes>(ADD_KANBAN_PARTICIPANT)
  const [removeParticipantMutation, { loading: removingParticipant }] =
    useMutation<IRemoveKanbanParticipantRes>(REMOVE_KANBAN_PARTICIPANT)
  const shouldFetchTaskDetail = show && mode === 'edit' && !!task?.id
  const {
    data: taskDetailData,
    loading: loadingTaskDetail,
    refetch: refetchTaskDetail,
  } = useQuery<IKanbanTaskDetailRes>(GET_KANBAN_TASK_DETAIL, {
    skip: !shouldFetchTaskDetail,
    variables: { id: task?.id ?? '' },
    fetchPolicy: 'network-only',
  })
  const shouldLoadParticipants = mode === 'edit' && canManageParticipants
  const { data: usersData } = useQuery(GET_ALL_USERS_LIST, {
    variables: { first: 200, isActive: true },
    skip: !shouldLoadParticipants,
  })
  const existingComments = taskDetailData?.kanbanTask?.comments ?? []
  const existingAttachments = taskDetailData?.kanbanTask?.attachments ?? []
  const existingChecklistItems = taskDetailData?.kanbanTask?.checklistItems ?? []
  const existingParticipants = taskDetailData?.kanbanTask?.participants ?? []
  const participantOptions = usersData?.allUsersList?.map((edge: any) => edge) ?? []
  const attachmentIconClass = (fileName?: string, contentType?: string) => {
    const ext = (fileName?.split('.').pop() ?? '').toLowerCase()
    if (
      contentType?.startsWith('image') ||
      ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)
    ) {
      return 'bi-file-earmark-image text-primary'
    }
    if (ext === 'pdf') return 'bi-filetype-pdf text-danger'
    if (ext === 'txt') return 'bi-filetype-txt text-muted'
    if (ext === 'doc' || ext === 'docx') return 'bi-file-earmark-word text-primary'
    if (ext === 'xls' || ext === 'xlsx') return 'bi-file-earmark-excel text-success'
    return 'bi-paperclip text-muted'
  }

  useEffect(() => {
    if (taskDetailData?.kanbanTask) {
      setIsTaskCompleted(!!taskDetailData.kanbanTask.isCompleted)
      setCompletedAtLabel(taskDetailData.kanbanTask.completedAt ?? null)
    }
  }, [taskDetailData?.kanbanTask?.isCompleted, taskDetailData?.kanbanTask?.completedAt])

  useEffect(() => {
    if (!show) return
    setAttachments([])
    setPendingComments([])
    setNewChecklistTitle('')
    if (mode === 'create') {
      setTitle('')
      setDescription('')
      setDueDate(null)
      setTagColor('')
      setLabelText('')
      setSelectedGroupId(null)
      setPriority('NORMAL')
      setIsTaskCompleted(false)
      setCompletedAtLabel(null)
    } else if (mode === 'edit' && task) {
      setTitle(task.title)
      setDescription(task.description ?? '')
      setDueDate(task.dueDate ? new Date(task.dueDate) : null)
      setTagColor(task.tagColor || '')
      setLabelText(task.labelText || '')
      setSelectedGroupId(task.assignmentGroup?.id ?? null)
      setPriority(task.priority || 'NORMAL')
      setIsTaskCompleted(!!task.isCompleted)
      setCompletedAtLabel(task.completedAt ?? null)
    }
  }, [show, mode, task])

  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => URL.revokeObjectURL(attachment.preview))
    }
  }, [attachments])

  const handleClose = () => {
    if (creating || updating) return
    onClose()
  }

  const handleFilesSelected = (fileList: FileList | File[]) => {
    const newFiles = Array.from(fileList || []).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      description: '',
    }))
    setAttachments((prev) => [...prev, ...newFiles])
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (event.dataTransfer?.files?.length) {
      handleFilesSelected(event.dataTransfer.files)
    }
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile()
        if (file) files.push(file)
      }
    }

    if (files.length > 0) {
      handleFilesSelected(files)
    }
  }

  const refreshExistingTaskData = () => {
    if (shouldFetchTaskDetail) {
      refetchTaskDetail()
    }
  }

  const handleAddChecklistItem = async () => {
    if (!task?.id) return
    if (!newChecklistTitle.trim()) {
      toast.error('Enter sub task')
      return
    }
    try {
      const { data } = await addChecklistItemMutation({
        variables: { taskId: parseId(task.id), title: newChecklistTitle.trim() },
      })
      if (!data?.addKanbanChecklistItem?.ok) {
        toast.error(data?.addKanbanChecklistItem?.errors?.[0] || 'The subtask could not be added')
        return
      }
      toast.success('Sub task added')
      setNewChecklistTitle('')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error - The subtask could not be added')
    }
  }

  const handleToggleChecklistItem = async (itemId: string, value: boolean) => {
    setTogglingChecklistId(itemId)
    try {
      const { data } = await toggleChecklistItemMutation({
        variables: { id: parseId(itemId), isCompleted: value },
      })
      if (!data?.toggleKanbanChecklistItem?.ok) {
        toast.error(
          data?.toggleKanbanChecklistItem?.errors?.[0] || 'The subtask could not be updated'
        )
        setTogglingChecklistId(null)
        return
      }
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error - The subtask could not be updated')
    } finally {
      setTogglingChecklistId(null)
    }
  }

  const startEditingChecklist = (itemId: string, title: string) => {
    setEditingChecklist({ id: itemId, title })
  }

  const saveChecklistEdit = async () => {
    if (!editingChecklist) return
    if (!editingChecklist.title.trim()) {
      toast.error('The subtask name cannot be empty.')
      return
    }
    try {
      const { data } = await updateChecklistItemMutation({
        variables: { id: parseId(editingChecklist.id), title: editingChecklist.title.trim() },
      })
      if (!data?.updateKanbanChecklistItem?.ok) {
        toast.error(
          data?.updateKanbanChecklistItem?.errors?.[0] || 'The subtask could not be updated'
        )
        return
      }
      setEditingChecklist(null)
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('The subtask could not be updated')
    }
  }

  const handleCopyLink = () => {
    if (!task?.id) return
    const url = new URL(window.location.origin + window.location.pathname)
    url.searchParams.set('task', task.id)
    navigator.clipboard.writeText(url.toString())
    toast.success('Link copied to clipboard')
  }

  const renderCommentBody = (text: string) => {
    if (!text) return null
    // Extract emails starting with @
    const parts = text.split(/(@[\w\.-]+@[\w\.-]+\.\w+)/g)
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.includes('@', 1)) {
        return (
          <span
            key={i}
            className='badge bg-info-subtle text-info-emphasis border border-info-subtle'
            style={{ fontWeight: 500 }}
          >
            {part}
          </span>
        )
      }
      return part
    })
  }

  const handleChecklistKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveChecklistEdit()
    } else if (event.key === 'Escape') {
      setEditingChecklist(null)
    }
  }

  const handleDeleteChecklistItem = async (itemId: string) => {
    if (!window.confirm('Delete sub tassk?')) return
    try {
      const { data } = await deleteChecklistItemMutation({ variables: { id: parseId(itemId) } })
      if (!data?.deleteKanbanChecklistItem?.ok) {
        toast.error(data?.deleteKanbanChecklistItem?.errors?.[0] || 'Error deleting the sub task')
        return
      }
      toast.success('Sub task deleted')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error deleting the sub task')
    }
  }
  const handleCompleteTaskToggle = async (value: boolean) => {
    if (!task?.id) return
    if (value && !window.confirm('Do you want to complete this subtask?')) {
      return
    }
    try {
      const { data } = await completeTaskMutation({
        variables: { id: parseId(task.id), isCompleted: value },
      })
      if (!data?.completeKanbanTask?.ok) {
        toast.error(data?.completeKanbanTask?.errors?.[0] || 'The task could not be updated')
        return
      }
      setIsTaskCompleted(value)
      setCompletedAtLabel(data.completeKanbanTask.task?.completedAt ?? null)
      toast.success(value ? 'Task is completed' : 'Task is pending')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('The task could not be updated')
    }
  }

  const handleAddParticipant = async () => {
    if (!task?.id || !selectedParticipantId) return
    try {
      const { data } = await addParticipantMutation({
        variables: { taskId: parseId(task.id), userId: parseId(selectedParticipantId) },
      })
      if (!data?.addKanbanParticipant?.ok) {
        toast.error(data?.addKanbanParticipant?.errors?.[0] || 'Error adding the participant')
        return
      }
      toast.success('Participant added')
      setSelectedParticipantId('')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error adding the participant')
    }
  }

  const handleRemoveParticipant = async (userId: string) => {
    if (!task?.id) return
    if (!window.confirm('Delete this participant?')) return
    try {
      const { data } = await removeParticipantMutation({
        variables: { taskId: parseId(task.id), userId: parseId(userId) },
      })
      if (!data?.removeKanbanParticipant?.ok) {
        toast.error(data?.removeKanbanParticipant?.errors?.[0] || 'Error removing the participant')
        return
      }
      toast.success('Participant removed')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error removing the participant')
    }
  }

  const handleDeleteExistingAttachment = async (attachmentId: string) => {
    if (!attachmentId) return
    try {
      const { data } = await deleteAttachmentMutation({ variables: { id: parseId(attachmentId) } })
      if (!data?.deleteKanbanTaskAttachment?.ok) {
        toast.error(
          data?.deleteKanbanTaskAttachment?.errors?.[0] || 'Error deleting the attachment'
        )
        return
      }
      toast.success('Attach deleted')
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error deleting the attachment')
    }
  }

  const handleStartEditComment = (commentId: string, body: string) => {
    setEditingComment({ id: commentId, body })
  }

  const handleSaveCommentEdit = async () => {
    if (!editingComment) return
    if (!editingComment.body.trim()) {
      toast.error('Comment body cannot be empty.')
      return
    }
    try {
      const { data } = await updateCommentMutation({
        variables: { id: parseId(editingComment.id), body: editingComment.body.trim() },
      })
      if (!data?.updateKanbanComment?.ok) {
        toast.error(data?.updateKanbanComment?.errors?.[0] || 'Error updating the comment')
        return
      }
      toast.success('Comment updated')
      setEditingComment(null)
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error updating the comment')
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!commentId) return
    try {
      const { data } = await deleteCommentMutation({ variables: { id: parseId(commentId) } })
      if (!data?.deleteKanbanComment?.ok) {
        toast.error(data?.deleteKanbanComment?.errors?.[0] || 'Error deleting the comment')
        return
      }
      toast.success('Comment deleted')
      if (editingComment?.id === commentId) {
        setEditingComment(null)
      }
      refreshExistingTaskData()
    } catch (error) {
      console.error(error)
      toast.error('Error deleting the comment')
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()

    const workflowNodeId = mode === 'create' ? workflowId : task?.workflow?.id
    const stageNodeId = mode === 'create' ? stage?.id : task?.stage?.id

    if (!workflowNodeId || !stageNodeId) {
      toast.error('Selected workflow or stage is missing')
      return
    }

    const workflowNumeric = parseId(workflowNodeId)
    const stageNumeric = parseId(stageNodeId)

    if (!workflowNumeric || !stageNumeric) {
      toast.error('IDs inválidos para el flujo o la columna')
      return
    }

    const assignmentGroupValue = selectedGroupId ? parseId(selectedGroupId) : null

    try {
      let taskId = ''
      if (mode === 'create') {
        const { data } = await createTask({
          variables: {
            workflowId: workflowNumeric,
            stageId: stageNumeric,
            assignmentGroupId: assignmentGroupValue,
            title,
            description: description || null,
            dueDate: dueDate ? dueDate.toISOString() : null,
            tagColor: tagColor || null,
            labelText: labelText || null,
            priority,
          },
        })
        if (!data?.createKanbanTask?.ok || !data.createKanbanTask.task?.id) {
          toast.error(data?.createKanbanTask?.errors?.[0] || 'Error creating the task')
          return
        }
        taskId = data.createKanbanTask.task.id
      } else if (mode === 'edit' && task) {
        const { data } = await updateTask({
          variables: {
            id: parseId(task.id),
            title,
            description: description || null,
            dueDate: dueDate ? dueDate.toISOString() : null,
            tagColor: tagColor || null,
            labelText: labelText || null,
            assignmentGroupId: assignmentGroupValue,
            priority,
          },
        })
        if (!data?.updateKanbanTask?.ok) {
          toast.error(data?.updateKanbanTask?.errors?.[0] || 'Error updating the task')
          return
        }
        taskId = String(parseId(task.id))
      }

      if (taskId && attachments.length) {
        const numericTaskId = taskId.includes(':') ? parseId(taskId) : taskId
        await Promise.all(
          attachments.map((attachment) =>
            uploadAttachment({
              variables: {
                taskId: numericTaskId,
                file: attachment.file,
                description: attachment.description || attachment.file.name,
              },
            })
          )
        )
      }

      if (taskId && pendingComments.length) {
        const numericTaskId = taskId.includes(':') ? parseId(taskId) : taskId
        await Promise.all(
          pendingComments.map((comment) =>
            createComment({
              variables: { taskId: numericTaskId, body: comment.text, isInternal: false },
            })
          )
        )
      }

      toast.success(mode === 'create' ? 'Task created succesfully' : 'Task updated succesfully')
      onSuccess()
      handleClose()
    } catch (error) {
      console.error(error)
      toast.error(mode === 'create' ? 'Error creating the task' : 'Error updating the task')
    }
  }

  const headerStageName =
    mode === 'create' ? stage?.name ?? 'column' : task?.stage?.name ?? 'column'

  return (
    <Modal show={show} onHide={handleClose} centered size='xl' className='kanban-task-modal'>
      <Form onSubmit={submit} onPaste={handlePaste}>
        <Modal.Header closeButton className='kanban-task-modal__header'>
          <Modal.Title className='d-flex align-items-center justify-content-between w-100 pe-5'>
            <span>{mode === 'create' ? 'New task' : 'Edit task'} in {headerStageName}</span>
            {readOnly && (
              <span className="badge bg-warning text-dark ms-2">Read Only</span>
            )}
            {mode === 'edit' && (
              <Button variant='outline-primary' size='sm' onClick={handleCopyLink} className='ms-auto'>
                <i className='bi bi-link-45deg me-1' /> Copy Link
              </Button>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-task-modal__body'>
          <div className='task-forms'>
            <Form.Group className='kanban-task-modal__group'>
              <Form.Label>
                <div className='task-title-complete'>
                  Task Name
                  {mode === 'edit' && (
                    <Form.Group className='kanban-task-modal__group d-flex align-items-center gap-3'>
                      <Form.Check
                        type='switch'
                        id='kanban-task-completed'
                        label='Completed'
                        checked={isTaskCompleted}
                        onChange={(e) => handleCompleteTaskToggle(e.target.checked)}
                        disabled={completingTask}
                      />
                      {isTaskCompleted && completedAtLabel && (
                        <span className='text-muted small'>
                          Completed at {new Date(completedAtLabel).toLocaleString()}
                        </span>
                      )}
                    </Form.Group>
                  )}
                </div>
              </Form.Label>
              <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} required disabled={readOnly} />
            </Form.Group>
            <Row className='kanban-task-modal__row g-3'>
              <Col md={12} className='task-detail'>
                <Form.Group className='kanban-task-modal__group'>
                  <Form.Label>Detail</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='Enter task details'
                    disabled={readOnly}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className='kanban-task-modal__row g-3'>
              <Col md={4}>
                {' '}
                <Form.Group className='kanban-task-modal__group'>
                  <Form.Label>Reference</Form.Label>
                  <Form.Control value={labelText} onChange={(e) => setLabelText(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className='kanban-task-modal__group'>
                  <Form.Label>Due date</Form.Label>
                  <div className='kanban-task-modal__datepicker'>
                    <DatePicker
                      value={dueDate}
                      onChange={(value) => {
                        if (!value) {
                          setDueDate(null)
                          return
                        }
                        if (value instanceof Date) {
                          setDueDate(value)
                          return
                        }
                        const maybeDate = (value as { toDate?: () => Date }).toDate?.()
                        if (maybeDate) setDueDate(maybeDate)
                      }}
                      format='YYYY-MM-DD'
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className='kanban-task-modal__group'>
                  <Form.Label>Prority</Form.Label>
                  <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                    <option value='LOW'>Low</option>
                    <option value='NORMAL'>Normal</option>
                    <option value='HIGH'>High</option>
                    <option value='URGENT'>Urgent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className='kanban-task-modal__group'>
                  <Form.Label>Color</Form.Label>
                  <div className='kanban-color-picker'>
                    <Form.Control
                      type='color'
                      className='kanban-task-modal__input kanban-task-modal__input--color'
                      value={tagColor || '#ffffff'}
                      onChange={(e) => setTagColor(e.target.value)}
                    />
                    <Button variant='link' size='sm' onClick={() => setTagColor('')}>
                      No Color
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Accordion className='accordion-task'>
              {/* PARTICIPANTES */}
              {canManageParticipants && mode === 'edit' && (
                <Accordion.Item eventKey='0'>
                  <Accordion.Header>Participants</Accordion.Header>
                  <Accordion.Body>
                    <div className='kanban-task-modal__participantes'>
                      <div className='kanban-task-modal__section'>
                        <div className='kanban-task-modal__participants-list'>
                          {existingParticipants.length === 0 ? (
                            <div className='text-muted'>There is not participants</div>
                          ) : (
                            existingParticipants.map((participant) => (
                              <div
                                key={participant.id}
                                className='kanban-task-modal__participant-pill'
                              >
                                <span>
                                  {participant.user.firstName} {participant.user.lastName}
                                </span>
                                <Button
                                  variant='link'
                                  size='sm'
                                  onClick={() => handleRemoveParticipant(participant.user.id)}
                                  disabled={removingParticipant}
                                >
                                  <i className='bi bi-x' />
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                        <div className='kanban-task-modal__participants-add'>
                          {!readOnly && (
                            <>
                              <Col md={9}>
                                <Form.Select
                                  size='sm'
                                  value={selectedParticipantId}
                                  onChange={(e) => setSelectedParticipantId(e.target.value)}
                                >
                                  <option value=''>Select user</option>
                                  {participantOptions
                                    ?.filter(
                                      (user: any) =>
                                        !existingParticipants.find((p) => p.user.id === user.id)
                                    )
                                    .map((user: any) => (
                                      <option key={user.id} value={user.id}>
                                        {user.firstName} {user.lastName} ({user.email})
                                      </option>
                                    ))}
                                </Form.Select>
                              </Col>
                              <Col md={3}>
                                <Button
                                  size='sm'
                                  variant='outline-primary'
                                  onClick={handleAddParticipant}
                                  disabled={addingParticipant || !selectedParticipantId}
                                >
                                  <i className='bi bi-plus-circle' />
                                  {addingParticipant ? 'Adding...' : ' Add participant'}
                                </Button>
                              </Col>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* SUBTASKS */}
              <Accordion.Item eventKey='1'>
                <Accordion.Header>Sub Tasks</Accordion.Header>
                <Accordion.Body>
                  {/* === TU BLOQUE DE SUBTASKS AQUÍ === */}
                  <div className='kanban-task-modal__subtask'>
                    {mode === 'edit' && (
                      <div className='kanban-task-modal__section'>
                        <div className='kanban-task-modal__section-header sub-task-section'>
                          {/* <span>Sub tasks</span> */}
                          <div className='sub-tasks-list'>
                            {loadingTaskDetail ? (
                              <div className='text-muted'>Loading...</div>
                            ) : existingChecklistItems.length === 0 ? (
                              <div className='text-muted no-sub-task'>There is not sub task.</div>
                            ) : (
                              <ul className='kanban-task-modal__checklist'>
                                {existingChecklistItems.map((item) => {
                                  const isEditingItem = editingChecklist?.id === item.id
                                  return (
                                    <li key={item.id} className='kanban-task-modal__checklist-item'>
                                      <Form.Check
                                        type='checkbox'
                                        id={`checklist-${item.id}`}
                                        className={`kanban-task-modal__checklist-check ${item.isCompleted
                                          ? 'kanban-task-modal__checklist-check--done'
                                          : ''
                                          }`}
                                        checked={item.isCompleted}
                                        onChange={(e) =>
                                          handleToggleChecklistItem(item.id, e.target.checked)
                                        }
                                        disabled={togglingChecklistId === item.id}
                                        label=''
                                      />
                                      {isEditingItem ? (
                                        <Form.Control
                                          size='sm'
                                          autoFocus
                                          value={editingChecklist.title}
                                          onChange={(e) =>
                                            setEditingChecklist((prev) =>
                                              prev ? { ...prev, title: e.target.value } : prev
                                            )
                                          }
                                          onKeyDown={handleChecklistKeyDown}
                                          onBlur={saveChecklistEdit}
                                        />
                                      ) : (
                                        <span
                                          className='kanban-task-modal__checklist-label'
                                          onClick={() => startEditingChecklist(item.id, item.title)}
                                        >
                                          {item.title}
                                        </span>
                                      )}
                                      <Button
                                        variant='link'
                                        size='sm'
                                        className='text-danger'
                                        onClick={() => handleDeleteChecklistItem(item.id)}
                                        disabled={deletingChecklist}
                                      >
                                        <i className='bi bi-trash' />
                                      </Button>
                                    </li>
                                  )
                                })}
                              </ul>
                            )}

                            <div className='kanban-task-modal__checklist-add'>
                              {!readOnly && (
                                <>
                                  <Form.Control
                                    size='sm'
                                    placeholder='New sub task'
                                    value={newChecklistTitle}
                                    onChange={(e) => setNewChecklistTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleAddChecklistItem()
                                      }
                                    }}
                                  />
                                  <Button
                                    size='sm'
                                    variant='outline-primary'
                                    onClick={handleAddChecklistItem}
                                    disabled={addingChecklist || !newChecklistTitle.trim()}
                                  >
                                    <i className='bi bi-plus-circle' />
                                    {addingChecklist ? 'Adding...' : ' Add'}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              {/* ADJUNTOS */}
              <Accordion.Item eventKey='2'>
                <Accordion.Header>Attachments</Accordion.Header>
                <Accordion.Body>
                  {/* === TU BLOQUE DE ADJUNTOS AQUÍ === */}
                  <div className='kanban-task-modal__attach'>
                    <div className='kanban-task-modal__section upload'>
                      {/* <div className='kanban-task-modal__section-header'>Adjuntos</div> */}
                      <div
                        className={`kanban-task-modal__dropzone ${isDragging ? 'kanban-task-modal__dropzone--active' : ''
                          }`}
                        onDragOver={(e) => {
                          e.preventDefault()
                          setIsDragging(true)
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault()
                          setIsDragging(false)
                        }}
                        onDrop={handleDrop}
                        onClick={() =>
                          document.getElementById('kanban-task-form-attachments')?.click()
                        }
                      >
                        <span>Drag & Drop files to upload</span>
                      </div>

                      <Form.Control
                        id='kanban-task-form-attachments'
                        type='file'
                        multiple
                        className='d-none'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          if (event.currentTarget.files) {
                            handleFilesSelected(event.currentTarget.files)
                          }
                        }}
                      />

                      {/* previews nuevos */}
                      {attachments.length > 0 && (
                        <div className='kanban-task-modal__attachments-list'>
                          {attachments.map((attachment, index) => (
                            <div
                              key={attachment.preview}
                              className='kanban-task-modal__attachment-item'
                            >
                              <div className='kanban-task-modal__attachment-info'>
                                {attachment.file.type.startsWith('image/') ? (
                                  <img src={attachment.preview} alt={attachment.file.name} />
                                ) : (
                                  <div className='kanban-task-modal__attachment-icon'>📄</div>
                                )}
                                <div>
                                  <div className='fw-bold'>{attachment.file.name}</div>
                                  <Form.Control
                                    size='sm'
                                    placeholder='Description'
                                    value={attachment.description ?? ''}
                                    onChange={(e) =>
                                      setAttachments((prev) => {
                                        const next = [...prev]
                                        next[index] = { ...next[index], description: e.target.value }
                                        return next
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <Button
                                variant='link'
                                size='sm'
                                className='text-danger'
                                onClick={() =>
                                  setAttachments((prev) => {
                                    const next = [...prev]
                                    const [removed] = next.splice(index, 1)
                                    removed && URL.revokeObjectURL(removed.preview)
                                    return next
                                  })
                                }
                              >
                                {' '}
                                <i className='bi bi-trash' />
                                Delete
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* adjuntos existentes */}
                    {mode === 'edit' && (
                      <div className='kanban-task-modal__section uploaded'>
                        <div className='kanban-task-modal__section-header'>Attachments</div>
                        {loadingTaskDetail ? (
                          <div className='text-muted'>Upload attachment...</div>
                        ) : existingAttachments.length === 0 ? (
                          <div className='text-muted'>There is no attachment.</div>
                        ) : (
                          <ul className='kanban-task-modal__existing-attachments'>
                            {existingAttachments.map((attachment) => (
                              <li
                                key={attachment.id}
                                className='kanban-task-modal__existing-attachment'
                              >
                                <div className='kanban-task-modal__existing-attachment-icon'>
                                  {attachment.contentType?.startsWith('image/') ? (
                                    <div className='kanban-task-modal__attachment-preview'>
                                      <img
                                        src={prependBackendUrl(attachment.file)}
                                        alt={attachment.originalName}
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <i
                                      className={`bi ${attachmentIconClass(
                                        attachment.originalName,
                                        attachment.contentType
                                      )}`}
                                    />
                                  )}
                                </div>
                                <div className='flex-grow-1'>
                                  <div className='d-flex align-items-center gap-2'>
                                    <a
                                      href={prependBackendUrl(attachment.file)}
                                      target='_blank'
                                      rel='noreferrer'
                                      className='fw-semibold'
                                    >
                                      {attachment.originalName || ''}
                                    </a>
                                    <a
                                      href={prependBackendUrl(attachment.file)}
                                      download={attachment.originalName}
                                      className='btn btn-link btn-sm p-0 text-muted'
                                      title='Download'
                                    >
                                      <i className='bi bi-download' />
                                    </a>
                                  </div>
                                  {attachment.description && (
                                    <div className='text-muted small'>{attachment.description}</div>
                                  )}
                                  <div className='text-muted small'>
                                    {attachment.uploadedBy?.firstName}{' '}
                                    {attachment.uploadedBy?.lastName} •{' '}
                                    {new Date(attachment.createdAt).toLocaleString()}
                                  </div>
                                </div>

                                <Button
                                  size='sm'
                                  variant='link'
                                  className='text-danger ms-2'
                                  onClick={() => handleDeleteExistingAttachment(attachment.id)}
                                >
                                  <i className='bi bi-trash' />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
          <div className='task-comments'>
            {mode === 'edit' && (
              <div className='kanban-task-modal__section'>
                <div className='kanban-task-modal__section-header'>Comments</div>
                {loadingTaskDetail ? (
                  <div className='text-muted'>Loading comments...</div>
                ) : existingComments.length === 0 ? (
                  <div className='text-muted'>There is no comments.</div>
                ) : (
                  <ul className='kanban-task-modal__existing-comments'>
                    {existingComments.map((comment) => {
                      const isEditing = editingComment?.id === comment.id
                      return (
                        <li key={comment.id} className='kanban-task-modal__comment-item'>
                          <div className='kanban-task-modal__comment-header title-comment'>
                            <div className='t-coment'>
                              <strong>
                                {comment.author
                                  ? `${comment.author.firstName} ${comment.author.lastName}`
                                  : 'User'}
                              </strong>
                              <span className='text-muted'>
                                {new Date(comment.createdAt).toLocaleString()}
                              </span>
                            </div>
                            {!comment.isInternal && (
                              <div className='kanban-task-modal__comment-actions'>
                                <Button
                                  size='sm'
                                  variant='link'
                                  className='text-edit ms-2'
                                  onClick={() => handleStartEditComment(comment.id, comment.body)}
                                >
                                  <i className='bi bi-pencil-square' />
                                </Button>
                                <Button
                                  size='sm'
                                  variant='link'
                                  className='text-danger ms-2'
                                  onClick={() => handleDeleteComment(comment.id)}
                                  disabled={deletingComment}
                                >
                                  <i className='bi bi-trash' />
                                </Button>
                              </div>
                            )}
                          </div>
                          {isEditing ? (
                            <>
                              <Form.Control
                                as='textarea'
                                rows={3}
                                value={editingComment.body}
                                onChange={(e) =>
                                  setEditingComment((prev) =>
                                    prev ? { ...prev, body: e.target.value } : prev
                                  )
                                }
                              />
                              <div className='kanban-task-modal__comment-actions'>
                                <Button
                                  size='sm'
                                  variant='primary'
                                  onClick={handleSaveCommentEdit}
                                  disabled={savingComment}
                                >
                                  {savingComment ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  size='sm'
                                  variant='light'
                                  onClick={() => setEditingComment(null)}
                                  disabled={savingComment}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className='kanban-task-modal__comment-body'>
                                {renderCommentBody(comment.body)}
                              </div>
                            </>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}

            <div className='kanban-task-modal__section'>
              <div className='kanban-task-modal__section-header d-flex align-items-center justify-content-between'>
                <span>Comments</span>
                <Button
                  size='sm'
                  variant='outline-primary'
                  onClick={() => setIsCommentModalOpen(true)}
                >
                  <i className='bi bi-plus-circle' /> Comments
                </Button>
              </div>
              {pendingComments.length === 0 ? (
                <div className='text-muted'>There is no comments.</div>
              ) : (
                <ul className='kanban-task-modal__pending-comments'>
                  {pendingComments.map((comment, index) => (
                    <li key={`${comment.createdAt.getTime()}-${index}`}>
                      <div className='title-comment'>
                        <div>
                          <strong>{comment.authorName}</strong>{' '}
                          <span className='text-muted'>({comment.createdAt.toLocaleString()})</span>
                        </div>
                        <div className='kanban-task-modal__comment-body'>
                          {renderCommentBody(comment.text)}
                        </div>
                        <Button
                          data-tooltip-content={'Eliminar'}
                          variant='link'
                          size='sm'
                          className='text-danger ms-2'
                          onClick={() =>
                            setPendingComments((prev) => prev.filter((_, idx) => idx !== index))
                          }
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      </div>

                      <div className='comment-text'>{comment.text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {false && (
            <Form.Group className='kanban-task-modal__group'>
              <Form.Label>Assignment Group</Form.Label>
              <Form.Select
                value={selectedGroupId ?? ''}
                onChange={(e) => setSelectedGroupId(e.target.value || null)}
              >
                <option value=''>No group</option>
                {availableGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className='kanban-task-modal__footer'>
          {!readOnly ? (
            <>
              <Button variant='light' onClick={handleClose} disabled={creating || updating}>
                Cancel
              </Button>
              <Button variant='primary' type='submit' disabled={creating || updating}>
                {mode === 'create'
                  ? creating
                    ? 'Adding...'
                    : 'Add task'
                  : updating
                    ? 'Updating...'
                    : 'Update task'}
              </Button>
            </>
          ) : (
            <Button variant='secondary' onClick={handleClose}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Form>
      <ModalAddComment
        show={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        onSave={(text) => {
          const authorName = currentUser
            ? `${currentUser.user.firstName} ${currentUser.user.lastName}`
            : 'Yo'
          setPendingComments((prev) => [...prev, { text, createdAt: new Date(), authorName }])
        }}
      />
    </Modal>
  )
}
